'use client'

import * as React from 'react'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import { Tooltip } from '~/components/component-library'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

const DropdownMenuContext = React.createContext<{
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}>({
  isOpen: false,
  setIsOpen: () => {},
})

const DropdownMenu = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>) => {
  const [internalOpen, setInternalOpen] = React.useState(false)

  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled && controlledOnOpenChange) {
        controlledOnOpenChange(value)
      } else {
        setInternalOpen(value)
      }
    },
    [isControlled, controlledOnOpenChange],
  )

  return (
    <DropdownMenuContext value={{ isOpen, setIsOpen }}>
      <DropdownMenuPrimitive.Root
        open={isOpen}
        onOpenChange={setIsOpen}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuContext>
  )
}

DropdownMenu.displayName = DropdownMenuPrimitive.Root.displayName

type DropdownMenuTriggerElement = React.ElementRef<
  typeof DropdownMenuPrimitive.Trigger
>

const DropdownMenuTrigger = ({
  ref,
  className,
  children,
  openOnContextMenu = false,
  onPointerDown,
  onContextMenu,
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
  className?: string
  openOnContextMenu?: boolean
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DropdownMenuPrimitive.Trigger
  > | null>
}) => {
  const isMobile = useIsMobile()
  const { isOpen, setIsOpen } = React.use(DropdownMenuContext)

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<DropdownMenuTriggerElement>) => {
      if (isMobile) {
        // Allow scrolling by preventing Radix from capturing pointer down on mobile.
        event.preventDefault()
      } else if (openOnContextMenu && event.button !== 2) {
        // Skip the default open-on-click behavior when we only want to open via context menu.
        event.preventDefault()
      }

      onPointerDown?.(event)
    },
    [isMobile, onPointerDown, openOnContextMenu],
  )

  const handleContextMenu = React.useCallback(
    (event: React.MouseEvent<DropdownMenuTriggerElement>) => {
      onContextMenu?.(event)

      if (!isMobile && openOnContextMenu) {
        event.preventDefault()
        setIsOpen(true)
      }
    },
    [isMobile, onContextMenu, openOnContextMenu, setIsOpen],
  )

  const handleClick = React.useCallback(
    (event: React.MouseEvent<DropdownMenuTriggerElement>) => {
      if (isMobile) {
        event.preventDefault()
        setIsOpen(!isOpen)
      }

      onClick?.(event)
    },
    [isMobile, isOpen, onClick, setIsOpen],
  )

  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      className={cn(className)}
      onPointerDown={handlePointerDown}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Trigger>
  )
}
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName

const DropdownMenuContent = ({
  ref,
  className,
  sideOffset = 6,
  onCloseAutoFocus,
  onPointerDown,
  onKeyDown,
  onPointerDownOutside,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  className?: string
  sideOffset?: number
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DropdownMenuPrimitive.Content
  > | null>
}) => {
  // Only return focus to the trigger when the menu is dismissed via keyboard.
  // A pointer dismissal (clicking an item or outside) leaves focus where the
  // click landed instead of yanking it back.
  const closedByPointerRef = React.useRef(false)

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'anchored z-50 min-w-[180px] overflow-hidden p-1',
          className,
        )}
        onPointerDown={(event) => {
          closedByPointerRef.current = true
          onPointerDown?.(event)
        }}
        onKeyDown={(event) => {
          closedByPointerRef.current = false
          onKeyDown?.(event)
        }}
        onPointerDownOutside={(event) => {
          closedByPointerRef.current = true
          onPointerDownOutside?.(event)
        }}
        onCloseAutoFocus={(event) => {
          if (closedByPointerRef.current) event.preventDefault()
          closedByPointerRef.current = false
          onCloseAutoFocus?.(event)
        }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = ({
  ref,
  className,
  inset,
  disabled,
  tooltip,
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  className?: string
  disabled?: boolean
  tooltip?: string
  onClick?: (event: React.MouseEvent) => void
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DropdownMenuPrimitive.Item
  > | null>
}) => {
  const item = (
    <DropdownMenuPrimitive.Item
      ref={ref}
      disabled={disabled}
      onClick={(event) => {
        if (disabled) return
        event.stopPropagation()
        onClick?.(event)
      }}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-normal outline-none transition-colors duration-100 ease-out active:brightness-[.9] data-[disabled]:pointer-events-none data-[highlighted]:bg-surface-strong data-[disabled]:opacity-50 [&>svg]:size-3.5',
        { 'pl-8': inset },
        className,
      )}
      {...props}
    />
  )
  if (tooltip) {
    return (
      <Tooltip triggerClassName="pointer-events-auto w-full" content={tooltip}>
        {item}
      </Tooltip>
    )
  }
  return item
}
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DropdownMenuPrimitive.Separator
  > | null>
}) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-divider', className)}
    {...props}
  />
)
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
}
